import React, { useState, useEffect } from 'react';
import { L } from '../router/Router';
import { C, F } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { MoonIcon, SunIcon, ChevronDownIcon } from './BrandIcons';
import { Menu, X, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = ({ onLoginClick, user }) => {
  const [sc, setSc] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setSc(window.scrollY > 50);
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    handleScroll();
    checkMobile();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const navLinks = [
    { label: 'Platform', to: '/platform' },
    { label: 'Intelligence', to: '/intelligence' },
    { label: 'Use Cases', to: '/use-cases' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Slidr', to: '/slidr' },
    { label: 'About', to: '/about' }
  ];

  const navStyle = {
    position: 'fixed',
    top: isMobile ? '8px' : '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: isMobile ? '95%' : '90%',
    maxWidth: '1200px',
    height: isMobile ? '62px' : '66px', // CHANGED: was 'auto' — fixed height prevents logo from stretching navbar
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: isMobile ? '0 12px' : '0 24px', // CHANGED: removed vertical padding since height is now fixed
    borderRadius: '100px',
    background: sc
      ? theme === 'dark'
        ? 'rgba(9, 10, 11, 0.95)'
        : 'rgba(255, 255, 255, 0.95)'
      : 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: `1px solid ${sc ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'}`,
    transition: 'all 0.3s ease',
    boxShadow: sc ? '0 8px 32px rgba(0,0,0,0.1)' : 'none'
  };

  const logoStyle = {
    fontFamily: F,
    fontSize: isMobile ? '18px' : '24px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    color: theme === 'dark' ? C.white : C.black,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    textDecoration: 'none'
  };

  const linkStyle = {
    color: theme === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
    fontSize: '14px',
    fontWeight: 600,
    textDecoration: 'none',
    padding: '8px 16px',
    borderRadius: '20px',
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  };

  return (
    <>
      <nav style={navStyle} data-testid="navbar">
        {/* Logo */}
        <L to="/" style={{ ...logoStyle, padding: 0, display: 'flex', alignItems: 'center' }}>
          <img
            src={theme === 'dark' ? '/logo-full-dark.svg' : '/logo-full-light.svg'}
            alt="DecX AI"
            style={{
              height: isMobile ? '100px' : '130px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </L>

        {/* Desktop Nav Links */}
        {!isMobile && (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {navLinks.map((link) => (
              <L
                key={link.to}
                to={link.to}
                style={linkStyle}
                onMouseEnter={(e) => {
                  e.target.style.background = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                }}
              >
                {link.label}
              </L>
            ))}
          </div>
        )}

        {/* Right side controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '12px' }}>
          {/* Theme toggle */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={toggleTheme}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: '50%',
              background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme === 'dark' ? C.white : C.black,
              flexShrink: 0
            }}
            data-testid="theme-toggle-btn"
          >
            {theme === 'dark' ? <SunIcon size={isMobile ? 16 : 20} /> : <MoonIcon size={isMobile ? 16 : 20} />}
          </motion.button>

          {/* User/Login Button */}
          {user ? (
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => isMobile ? onLoginClick?.() : setUserMenuOpen(!userMenuOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? '4px' : '6px 12px 6px 6px',
                borderRadius: '100px',
                background: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
                color: theme === 'dark' ? C.white : C.black,
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: F,
                flexShrink: 0
              }}
              data-testid="user-menu-btn"
            >
              {user.profile_picture ? (
                <img
                  src={user.profile_picture}
                  alt={user.name}
                  style={{
                    width: isMobile ? '24px' : '28px',
                    height: isMobile ? '24px' : '28px',
                    borderRadius: '50%',
                  }}
                />
              ) : (
                <div style={{
                  width: isMobile ? '24px' : '28px',
                  height: isMobile ? '24px' : '28px',
                  borderRadius: '50%',
                  background: C.secondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: 700,
                }}>
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
              {!isMobile && (
                <>
                  <span style={{ marginLeft: '8px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.name?.split(' ')[0] || 'User'}
                  </span>
                  <ChevronDownIcon size={14} style={{ opacity: 0.6, marginLeft: '4px' }} />
                </>
              )}
            </motion.button>
          ) : (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = `${process.env.REACT_APP_DECX_BASE_URL}/login-screen`} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? '6px' : '8px 14px',
                borderRadius: '100px',
                background: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
                color: theme === 'dark' ? C.white : C.black,
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: F,
                flexShrink: 0
              }}
              data-testid="login-btn"
            >
              <User size={isMobile ? 16 : 16} />
              {!isMobile && <span style={{ marginLeft: '6px' }}>Login</span>}
            </motion.button>
          )}

          {/* CTA Button - desktop only */}
          {!isMobile && (
            <L to="/score" style={{ textDecoration: 'none' }}>
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 8px 30px rgba(0,130,251,0.5)' }}
                whileTap={{ scale: 0.98 }}
                style={{
                  background: C.grad,
                  color: C.white,
                  padding: '10px 18px',
                  borderRadius: '100px',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontFamily: F,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
                data-testid="nav-cta-btn"
              >
                Get Free Score
              </motion.button>
            </L>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                padding: '6px',
                borderRadius: '8px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme === 'dark' ? C.white : C.black,
                flexShrink: 0
              }}
              data-testid="mobile-menu-btn"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </motion.button>
          )}
        </div>

        {/* Desktop User Dropdown */}
        {!isMobile && user && (
          <AnimatePresence>
            {userMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.95 }}
                style={{
                  position: 'absolute',
                  top: '72px',
                  right: '140px',
                  background: theme === 'dark' ? '#1a1a2e' : '#ffffff',
                  borderRadius: '12px',
                  border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                  minWidth: '200px',
                  overflow: 'hidden',
                  zIndex: 1001,
                }}
              >
                <div style={{
                  padding: '12px 16px',
                  borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                }}>
                  <p style={{ fontWeight: 600, fontSize: '14px', marginBottom: '2px', color: theme === 'dark' ? C.white : C.black }}>{user.name}</p>
                  <p style={{ fontSize: '12px', opacity: 0.6, color: theme === 'dark' ? C.white : C.black }}>{user.email}</p>
                </div>
                <button
                  onClick={() => { setMenuOpen(false); window.location.href = `${process.env.REACT_APP_DECX_BASE_URL}/login-screen`; }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: theme === 'dark' ? C.white : C.black,
                    fontSize: '14px',
                    textAlign: 'left',
                  }}
                >
                  <User size={16} />
                  Dashboard
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </nav>

      {/* Mobile Menu Fullscreen */}
      <AnimatePresence>
        {menuOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: theme === 'dark' ? 'rgba(9, 10, 11, 0.98)' : 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              zIndex: 999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '20px',
              padding: '20px'
            }}
            data-testid="mobile-menu-overlay"
          >
            {navLinks.map((link, i) => (
              <motion.div
                key={link.to}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <L
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: theme === 'dark' ? C.white : C.black,
                    textDecoration: 'none',
                    fontFamily: F
                  }}
                >
                  {link.label}
                </L>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: navLinks.length * 0.05 }}
              style={{ marginTop: '16px' }}
            >
              <button
                onClick={() => { setMenuOpen(false); window.location.href = `${process.env.REACT_APP_DECX_BASE_URL}/login-screen`; }}
                style={{
                  background: 'transparent',
                  color: C.secondary,
                  padding: '14px 28px',
                  borderRadius: '100px',
                  border: `2px solid ${C.secondary}`,
                  fontWeight: 700,
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: F
                }}
              >
                <User size={18} /> {user ? 'Dashboard' : 'Login'}
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (navLinks.length + 1) * 0.05 }}
            >
              <L to="/score" onClick={() => setMenuOpen(false)} style={{ textDecoration: 'none' }}>
                <button
                  style={{
                    background: C.grad,
                    color: C.white,
                    padding: '14px 28px',
                    borderRadius: '100px',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '16px',
                    cursor: 'pointer',
                    boxShadow: '0 8px 30px rgba(0,130,251,0.4)',
                    fontFamily: F
                  }}
                >
                  Get Free Score
                </button>
              </L>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};